/**
 * STALE 히스토그램 + 임계선 (RUNBOOK P4 위젯 3).
 *
 * 실측에서 `statUpdDt` 1일 초과가 44.3% 였다 (§15.3). 임계선을 함께 그려
 * **"이 선을 어디 긋느냐에 따라 모집단의 44%가 움직인다"** 는 사실을 드러낸다.
 */

import { useQuery } from '@tanstack/react-query'
import type { EChartsOption } from 'echarts'
import { useMemo } from 'react'

import { EChart } from '@/components/EChart'
import { CHART_H } from '@/lib/chart'
import { ChartFrame } from '@/components/ChartFrame'
import { Headline } from '@/components/WidgetShell'
import { fetchEnvelope } from '@/lib/api'
import { cssVar } from '@/lib/cssvar'
import type { Envelope, StaleBin } from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'

interface Data {
  bins: StaleBin[]
  stale_threshold_h: number | null
}

/** 로그10(분) → 사람이 읽는 라벨 */
function humanize(log10min: number): string {
  const minutes = Math.pow(10, log10min)
  if (minutes < 60) return `${Math.round(minutes)}분`
  if (minutes < 1440) return `${(minutes / 60).toFixed(1)}시간`
  return `${(minutes / 1440).toFixed(1)}일`
}

export function StaleHistogram() {
  const filters = useFilters()
  const q = useQuery({
    queryKey: ['quality/stale_histogram', filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<Data>('/quality/stale_histogram', toQueryFilters(filters), {}, signal),
    staleTime: 5 * 60 * 1000,
  })

  const env = q.data as Envelope<Data> | undefined
  const bins = useMemo(() => [...(env?.data.bins ?? [])].sort((a, b) => a.log10_min - b.log10_min), [env])
  const thresholdH = env?.data.stale_threshold_h ?? null

  const option = useMemo<EChartsOption>(() => {
    const thresholdLog = thresholdH != null ? Math.log10(thresholdH * 60) : null
    return {
      grid: { left: 48, right: 12, top: 16, bottom: 32 },
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const p = Array.isArray(params) ? params[0] : params
          const idx = (p as { dataIndex: number }).dataIndex
          const bin = bins[idx]
          if (!bin) return ''
          return `${humanize(bin.log10_min)} · ${bin.n.toLocaleString('ko-KR')}구간`
        },
      },
      xAxis: {
        type: 'category',
        data: bins.map((b) => humanize(b.log10_min)),
        axisLabel: { fontSize: 10, interval: Math.max(0, Math.floor(bins.length / 8)) },
      },
      yAxis: { type: 'value', axisLabel: { fontSize: 10 } },
      series: [
        {
          type: 'bar',
          data: bins.map((b) => b.n),
          itemStyle: { color: cssVar("--c-na", "#8a8a84") },
          markLine:
            thresholdLog != null
              ? {
                  silent: true,
                  symbol: 'none',
                  // 임계선은 **고장이 아니다.** DOWN 색을 쓰면 "이 선을 넘으면
                  // 고장"으로 읽힌다 — 상태색은 상태 인코딩 전용이다 (§8.6.2)
                  lineStyle: { color: cssVar('--c-ink-2', '#4a4a4f'), type: 'solid', width: 1.5 },
                  label: {
                    formatter: `임계 ${thresholdH}h`,
                    fontSize: 10,
                    color: cssVar('--c-ink-2', '#4a4a4f'),
                    rotate: 0,
                    distance: 4,
                  },
                  data: [
                    {
                      xAxis: bins.findIndex((b) => b.log10_min >= thresholdLog),
                    },
                  ],
                }
              : undefined,
        },
      ],
    }
  }, [bins, thresholdH])

  const totalStale = useMemo(() => bins.reduce((s, b) => s + b.n, 0), [bins])

  /** 표 뷰 — 로그 가로축은 눈으로 구간 경계를 읽을 수 없다 */
  const table = (
    <div className="ctable">
      <table className="data-table">
        <caption className="sr-only">STALE 구간 길이별 개수</caption>
        <thead>
          <tr>
            <th scope="col">구간 길이</th>
            <th scope="col" className="ta-r">
              구간 수
            </th>
            <th scope="col" className="ta-r">
              비율
            </th>
            <th scope="col">임계 대비</th>
          </tr>
        </thead>
        <tbody>
          {bins.map((b) => {
            const minutes = Math.pow(10, b.log10_min)
            const over = thresholdH != null && minutes >= thresholdH * 60
            return (
              <tr key={b.log10_min}>
                <th scope="row">{humanize(b.log10_min)}</th>
                <td className="num ta-r">{b.n.toLocaleString('ko-KR')}</td>
                <td className="num ta-r">
                  {totalStale > 0 ? `${((b.n / totalStale) * 100).toFixed(2)}%` : '—'}
                </td>
                <td>{thresholdH == null ? '—' : over ? '초과 (NA 로 격리)' : '이내'}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )

  return (
    <ChartFrame
      title="STALE 구간 길이 분포"
      question="사업자 연계가 끊긴 채 얼마나 오래 있었나"
      meta={env?.meta}
      hideCoverage
      headline={<Headline value={totalStale.toLocaleString('ko-KR')} unit="구간" />}
      reading={`상태 정보가 갱신되지 않은 채 방치된 구간이 각각 얼마나 길었는지 세었습니다. 세로선(임계 ${thresholdH ?? '?'}시간) 오른쪽에 있는 구간은 "너무 오래되어 믿을 수 없다"로 판단해 NA 로 격리한 몫입니다.`}
      encoding={[
        {
          role: '가로축',
          what: '구간 길이 (로그 눈금 · 분 → 시간 → 일)',
          detail:
            '길이가 몇 분에서 몇 년까지 벌어져 선형 눈금으로는 짧은 구간이 한 칸에 뭉칩니다. 눈금 한 칸이 10배이므로 가로 위치 차이를 눈으로 비례 해석하지 마세요.',
        },
        {
          role: '세로축',
          what: '해당 길이 구간의 개수',
          detail: '충전기 수가 아니라 구간(끊김 사건) 수입니다. 한 충전기가 여러 구간을 가질 수 있습니다.',
        },
        {
          role: '세로선',
          what: `STALE 임계 ${thresholdH ?? '?'}시간`,
          detail:
            'probe 가 분포의 골짜기를 탐색해 정한 값입니다 (게이트 D4). 이 선을 어디 긋느냐에 따라 모집단의 44%가 움직입니다.',
        },
      ]}
      isLoading={q.isLoading}
      error={q.error as Error | null}
      isEmpty={bins.length === 0}
      onRetry={() => void q.refetch()}
      table={table}
      notes={[
        {
          label: 'STALE 은 충전기 고장이 아니다.',
          text: '사업자→환경공단 연계가 끊긴 것입니다. 그 사이 충전기가 정상이었는지 고장이었는지 방향을 알 수 없어 NA 로 격리하고 분모에서 뺍니다.',
        },
        {
          label: '임계값이 결론을 바꾼다.',
          text: `실측에서 statUpdDt 가 1일을 넘긴 비율이 44.3% 였습니다 (§15.3). 임계 ${thresholdH ?? '?'}시간을 24시간에서 72시간으로 옮기면 NA 로 격리되는 시간이 크게 줄고 가동률이 달라집니다.`,
        },
        {
          label: '세로선 오른쪽이 잘린 구간이다.',
          text: '임계를 넘긴 구간은 그 지점에서 끊고 이후를 NA 로 둡니다. 이때 진행 중이던 고장 세션은 종료가 아니라 중도절단으로 셉니다 (§3.3.1.1).',
        },
      ]}
    >
      <EChart
        option={option}
        height={CHART_H}
        ariaLabel={`STALE 구간 길이 히스토그램. 총 ${totalStale.toLocaleString('ko-KR')}구간, 임계 ${thresholdH ?? '미정'}시간`}
      />
      <p className="num" style={{ fontSize: 'var(--fs-xs)', color: 'var(--c-text-muted)' }}>
        STALE 구간 {totalStale.toLocaleString('ko-KR')}개
      </p>
    </ChartFrame>
  )
}
